// React, React Router DOM y React Hook Form
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { SubmitHandler } from "react-hook-form";
import type { LoginForm, LoginProps } from "./types";
import { useState, useEffect } from "react";

// Librerias externas
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import { useUserStore } from "../../store/userStore";
import { useTranslation } from "react-i18next";
import { setLoggingOut } from "../../utils/tokenUtils";
import Swal from "sweetalert2";
import api from "../../utils/api";
import { brand } from "../../config/theme";

// Componentes
// import Button from "../../components/base/Button";
import GreenButton from "../../components/base/GreenButton";

// import CustomAlert from "../../components/base/CustomAlert";

// Funciones
import { toggleSwitch, authenticateUser } from "./functions";
// import { resetAlert } from "../../../helpers";

// Iconos
import { LogoSplash } from "../../config/brandAssets";
// import TdcIcon from "../../../assets/images/tdc_logo2.png";

// Context
import { useTheme } from "../../context/ThemeContext";

// Estilos
import {
  ContainerLogin,
  ComponentTitle,
  ContainerForm,
  Form,
  ContainerLabelInput,
  Label,
  ContainerInput,
  Input,
  ContainerInputPassword,
  InputPassword,
  EyeButton,
  Errors,
  ContainerButtons,
  LinkButton,
  ContainerSwitch,
  Switch,
  Language,
  ComponentIcon,
} from "./styles";
import { useAlert } from "../../context/AlertContext";

type ViewState = "login" | "forgot";

interface ForgotPasswordForm {
  email: string;
  website?: string; // Campo honeypot - debe estar vacío
}

function Login({ isEnglish, setIsEnglish }: LoginProps) {
  // Hooks
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  //   const dispatch = useDispatch();

  // i18n
  const { i18n } = useTranslation();
  const { t } = useTranslation(["login", "common"]);

  const { loading } = useUserStore();

  // Estados Locales y Globales
  const { showAlert } = useAlert();
  const [showPassword, setShowPassword] = useState(false);
  const [view, setView] = useState<ViewState>("login");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  // Resetear flag de logout al cargar la página de login
  useEffect(() => {
    setLoggingOut(false);
  }, []);

  // Contador regresivo para rate limiting
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      const timer = setTimeout(() => {
        setRateLimitCountdown(rateLimitCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitCountdown]);

  // React Hook Form para recuperación de contraseña
  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    formState: { errors: forgotErrors },
    reset: resetForgot,
  } = useForm<ForgotPasswordForm>({ mode: "onBlur" });

  // Función para solicitar recuperación de contraseña
  const onForgotSubmit: SubmitHandler<ForgotPasswordForm> = async (data) => {
    setForgotLoading(true);
    try {
      await api.post("/auth/request-password-reset", {
        email: data.email.toLowerCase(),
        website: data.website, // Campo honeypot
      });

      await Swal.fire({
        icon: "success",
        title: t("forgotPasswordSuccess"),
        text: t("forgotPasswordEmailSent"),
        confirmButtonText: t("common:general.accept"),
        confirmButtonColor: brand.primary,
      });

      resetForgot();
      setView("login");
    } catch (error: unknown) {
      // Verificar si es error de rate limiting (429)
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 429) {
        // Iniciar contador de 5 minutos (300 segundos)
        setRateLimitCountdown(300);
        await Swal.fire({
          icon: "warning",
          title: t("common:general.warning"),
          text: t("rateLimitMessage"),
          confirmButtonText: t("common:general.accept"),
          confirmButtonColor: brand.primary,
        });
        setForgotLoading(false);
        return;
      }

      // Por seguridad, el backend siempre responde igual
      // pero mostramos mensaje de éxito de todos modos
      await Swal.fire({
        icon: "success",
        title: t("forgotPasswordSuccess"),
        text: t("forgotPasswordEmailSent"),
        confirmButtonText: t("common:general.accept"),
        confirmButtonColor: brand.primary,
      });

      resetForgot();
      setView("login");
    } finally {
      setForgotLoading(false);
    }
  };

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    // watch,
    formState: { errors },
  } = useForm<LoginForm>({ mode: "onBlur" });
  //   const watchAllFields = watch();

  // Función para enviar el formulario
  const onSubmit: SubmitHandler<LoginForm> = async (data) => {
    await authenticateUser(
      data.emailOrUsername,
      data.password,
      navigate,
      showAlert,
      t,
    );
  };

  // Render del formulario de recuperación de contraseña
  const renderForgotPasswordForm = () => (
    <ContainerLogin>
      <div className="flex flex-col">
        <ComponentIcon>
          <img src={LogoSplash} alt="" />
        </ComponentIcon>
        <ComponentTitle>{t("recoverPassword").toUpperCase()}</ComponentTitle>
      </div>
      <ContainerForm>
        <Form onSubmit={handleSubmitForgot(onForgotSubmit)}>
          <p className="text-gray-400 text-sm text-center mb-4">
            {t("forgotPasswordDescription")}
          </p>
          {/* Campo Honeypot - Oculto para usuarios, visible para bots */}
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              opacity: 0,
              height: 0,
              overflow: "hidden",
            }}
            aria-hidden="true"
          >
            <label htmlFor="website">Website</label>
            <input
              {...registerForgot("website")}
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <ContainerLabelInput>
            <Label htmlFor="forgotEmail">{t("email")}:</Label>
            <ContainerInput>
              <Input
                {...registerForgot("email", {
                  required: "requiredField",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "invalidEmail",
                  },
                })}
                type="email"
                id="forgotEmail"
                name="email"
                placeholder={t("placeholderEmail")}
                styleprop={forgotErrors?.email}
              />
              {forgotErrors?.email && (
                <Errors>{t(forgotErrors?.email?.message || "")}</Errors>
              )}
            </ContainerInput>
          </ContainerLabelInput>

          <ContainerButtons>
            <GreenButton
              type="submit"
              disabled={forgotLoading || rateLimitCountdown > 0}
              className={
                forgotLoading || rateLimitCountdown > 0
                  ? "bg-green-700 hover:bg-green-700"
                  : ""
              }
            >
              {forgotLoading ? (
                <span className="flex items-center justify-center">
                  <span className="w-5 h-5 border-2 border-green-800 border-t-transparent rounded-full animate-spin mr-2"></span>
                  <span className="text-white">
                    {t("common:general.loading")}
                  </span>
                </span>
              ) : rateLimitCountdown > 0 ? (
                <span className="flex items-center justify-center">
                  <span className="w-5 h-5 border-2 border-green-800 border-t-transparent rounded-full animate-spin mr-2"></span>
                  <span className="text-white">
                    {t("waitMinutes", {
                      minutes: Math.ceil(rateLimitCountdown / 60),
                    })}
                  </span>
                </span>
              ) : (
                t("sendRecoveryEmail")
              )}
            </GreenButton>
            <LinkButton type="button" onClick={() => setView("login")}>
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              {t("backToLogin")}
            </LinkButton>
          </ContainerButtons>
        </Form>
      </ContainerForm>
      <ContainerSwitch>
        <Switch>
          <input
            type="checkbox"
            value={isEnglish ? "true" : "false"}
            className="sr-only peer"
            onChange={() =>
              toggleSwitch(isEnglish ? "es" : "en", setIsEnglish, i18n)
            }
            checked={!isEnglish}
          />
          <div className="w-11 h-6 bg-[#767a7c] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full  after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-[var(--brand-primary)]"></div>
          <Language>{isEnglish ? "Eng" : "Esp"}</Language>
        </Switch>
      </ContainerSwitch>
    </ContainerLogin>
  );

  // Render del formulario de login
  const renderLoginForm = () => (
    <ContainerLogin>
      <div className="flex flex-col">
        <ComponentIcon>
          <img src={LogoSplash} alt="" />
        </ComponentIcon>
        <ComponentTitle>{t("welcome").toUpperCase()}</ComponentTitle>
      </div>
      <ContainerForm>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ContainerLabelInput>
            <Label htmlFor="emailOrUsername">{t("emailOrUsername")}:</Label>
            <ContainerInput>
              <Input
                {...register("emailOrUsername", {
                  required: "requiredField",
                  minLength: {
                    value: 3,
                    message: "minimumCharacters",
                  },
                })}
                type="text"
                id="emailOrUsername"
                name="emailOrUsername"
                placeholder={t("placeholderEmailOrUsername")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue("emailOrUsername", e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  })
                }
                styleprop={errors?.emailOrUsername}
              />
              {errors?.emailOrUsername && (
                <Errors>{t(errors?.emailOrUsername?.message || "")}</Errors>
              )}
            </ContainerInput>
          </ContainerLabelInput>

          <ContainerLabelInput>
            <Label htmlFor="password">{t("password")}:</Label>
            <ContainerInput>
              <ContainerInputPassword styleprop={errors?.password}>
                <InputPassword
                  {...register("password", {
                    required: "requiredField",
                    minLength: {
                      value: 6,
                      message: "minimumCharacters",
                    },
                  })}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder={t("placeholderPassword")}
                />
                <EyeButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon
                    icon={showPassword ? faEye : faEyeSlash}
                    color={isDarkMode ? "#9ca3af" : "#4d5254"}
                    className="text-2xl"
                  />
                </EyeButton>
              </ContainerInputPassword>
              {errors?.password && (
                <Errors>{t(errors.password.message || "")}</Errors>
              )}
            </ContainerInput>
          </ContainerLabelInput>

          <ContainerButtons>
            <GreenButton
              type="submit"
              disabled={loading}
              className={loading ? "bg-green-700 hover:bg-green-700" : ""}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="w-5 h-5 border-2 border-green-800 border-t-transparent rounded-full animate-spin mr-2"></span>
                  <span className="text-white">
                    {t("common:general.loading")}
                  </span>
                </span>
              ) : (
                t("login")
              )}
            </GreenButton>
            <LinkButton type="button" onClick={() => setView("forgot")}>
              {t("forgotPassword")}
            </LinkButton>
          </ContainerButtons>
        </Form>
      </ContainerForm>
      <ContainerSwitch>
        <Switch>
          <input
            type="checkbox"
            value={isEnglish ? "true" : "false"}
            className="sr-only peer"
            onChange={() =>
              toggleSwitch(isEnglish ? "es" : "en", setIsEnglish, i18n)
            }
            checked={!isEnglish}
          />
          <div className="w-11 h-6 bg-[#767a7c] rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full  after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-[var(--brand-primary)]"></div>
          <Language>{isEnglish ? "Eng" : "Esp"}</Language>
        </Switch>
      </ContainerSwitch>
    </ContainerLogin>
  );

  return view === "login" ? renderLoginForm() : renderForgotPasswordForm();
}

export default Login;
